import React, { useState, useEffect } from 'react';
import ArtistLayout from '../../components/artist/ArtistLayout';
import { Package, Calendar, DollarSign } from 'lucide-react';
import { apiRequest } from '../../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function ArtistOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('artist_token');
      
      const response = await apiRequest(`${BACKEND_URL}/api/artist/cards/orders`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setOrders(response.data.orders || []);
      }
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch orders:', err);
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      ordered: 'bg-blue-100 text-blue-800',
      processing: 'bg-yellow-100 text-yellow-800',
      dispatched: 'bg-purple-100 text-purple-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      refunded: 'bg-gray-100 text-gray-800'
    };
    
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${colors[status] || colors.ordered}`}>
        {status}
      </span>
    );
  };

  return (
    <ArtistLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-navy mb-2">Orders</h1>
          <p className="text-gray-600">Orders placed on your cards</p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading orders...</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl shadow-sm">
            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No orders yet</p>
            <p className="text-sm text-gray-500 mt-2">Orders will appear here when customers purchase your cards</p>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <div
                key={order.transaction_id}
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-navy mb-1">
                      Order #{order.transaction_id?.slice(0, 8)}
                    </h3>
                    <p className="text-gray-600">
                      {order.order_data?.card?.name || 'Card'}
                    </p>
                  </div>
                  {getStatusBadge(order.order_status)}
                </div>
                
                <div className="grid md:grid-cols-3 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">
                      {new Date(order.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">
                      ₹{order.amount}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">
                      Qty: {order.order_data?.quantity || 1}
                    </span>
                  </div>
                </div>
                
                {order.shipping_address && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <p className="text-xs font-semibold text-gray-600 mb-1">Shipping To:</p>
                    <p className="text-sm text-gray-700">
                      {order.shipping_address.firstName} {order.shipping_address.lastName}
                      <br />
                      {order.shipping_address.city}, {order.shipping_address.state}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </ArtistLayout>
  );
}